import React, { useState, useEffect } from 'react';
import { X, Search, Check, ChevronDown, ChevronRight, Trash2, Baby, BookOpen, School, Building, GraduationCap } from 'lucide-react';
import { api } from '../services/api';
import ChampSelect from './ui/ChampSelect';
import { Toast } from './ui/Toast';

// Radix n'accepte pas la chaine vide comme valeur d'option : le choix
// « tous » porte donc un nom, traduit en chaine vide a la sortie - c'est
// elle que le reste de l'ecran teste.
const TOUS_LES_ENSEIGNANTS = 'tous';

/**
 * La case a cocher de l'ecran : celle de la fenetre d'export, a la couleur
 * de celui-ci.
 *
 * Un vrai <input> dessous - lisible au clavier et par un lecteur d'ecran -
 * et la case dessinee par-dessus. Trois formes cohabitaient dans le module :
 * une case native du navigateur, un <svg> ecrit a la main, et celle-ci.
 * Elles n'en font plus qu'une.
 */
const CaseACocher = ({ cochee, onChange, label, className = '' }) => (
  <span className={`relative flex items-center ${className}`}>
    <input
      type="checkbox"
      checked={cochee}
      onChange={onChange}
      aria-label={label}
      className="sr-only"
    />
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${cochee
        ? 'border-transparent bg-[#c66d59]'
        : 'border-gray-300 hover:border-[#c66d59]'}`}
    >
      {cochee && <Check className="text-white" size={13} />}
    </span>
  </span>
);

const DeleteBulkTimetableModal = ({ 
  isOpen, 
  onClose, 
  classes, 
  sections,
  activeYear,
  onDeleteSuccess
}) => {
  // ========== ÉTATS TOAST ==========
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    
    // Auto-remove après 5 secondes
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const [selectedClasses, setSelectedClasses] = useState([]);
  const [porteeType, setPorteeType] = useState('annee');
  const [trimestre, setTrimestre] = useState('1');
  const [moisId, setMoisId] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [previewCount, setPreviewCount] = useState(null);
  // Suppression ciblee : vide = tous les enseignants, comme avant.
  const [enseignantId, setEnseignantId] = useState('');
  const [enseignants, setEnseignants] = useState([]);
  const [previewMutualises, setPreviewMutualises] = useState(0);
  // Les cours partages concernes, et ceux dont on retire la classe visee.
  const [groupesMutualises, setGroupesMutualises] = useState([]);
  const [groupesARetirer, setGroupesARetirer] = useState([]);
  
  const [moisScolaires, setMoisScolaires] = useState([]);
  const [loadingMois, setLoadingMois] = useState(false);
  // Le bouton demande confirmation avant d'effacer, et se verrouille
  // pendant l'envoi.
  const [confirmation, setConfirmation] = useState(false);
  const [enSuppression, setEnSuppression] = useState(false);

  // Charger les mois scolaires
  useEffect(() => {
    const loadMoisScolaires = async () => {
      if (!activeYear) return;
      
      setLoadingMois(true);
      try {
        const response = await api.moisScolaires.getAll(activeYear.id);
        if (response.success && Array.isArray(response.data)) {
          setMoisScolaires(response.data);
        }
      } catch (error) {
        console.error('Erreur chargement mois scolaires:', error);
      } finally {
        setLoadingMois(false);
      }
    };

    if (isOpen) {
      loadMoisScolaires();
    }
  }, [isOpen, activeYear]);

  const getSectionIcon = (sectionName) => {
    const name = sectionName.toLowerCase();
    if (name.includes('préscolaire') || name.includes('prescolaire') || name.includes('maternelle')) {
      return <Baby className="w-4 h-4" />;
    }
    if (name.includes('primaire') || name.includes('élémentaire')) {
      return <BookOpen className="w-4 h-4" />;
    }
    if (name.includes('collège') || name.includes('college')) {
      return <School className="w-4 h-4" />;
    }
    if (name.includes('lycée') || name.includes('lycee')) {
      return <Building className="w-4 h-4" />;
    }
    return <GraduationCap className="w-4 h-4" />;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const toggleClassSelection = (classeId) => {
    setSelectedClasses(prev => {
      if (prev.includes(classeId)) {
        return prev.filter(id => id !== classeId);
      } else {
        return [...prev, classeId];
      }
    });
  };

  // ⚠️ « Tout selectionner » cochait `classes` EN ENTIER, alors que la
  // liste ne rend que les classes d'une section connue, filtrees par la
  // recherche. Une classe sans section n'apparait JAMAIS a l'ecran et se
  // serait retrouvee supprimee. Le bouton ne peut cocher que ce qu'on voit.
  // ⚠️ UN SEUL terme, lu par le bouton ET par la liste. Ils divergeaient :
  // celui-ci coupait les espaces, la liste non. Taper « 6e » puis une espace
  // vidait l'affichage, et « Tout selectionner » cochait quand meme les
  // classes que la version coupee trouvait - des classes jamais vues, armees
  // pour la suppression.
  const classesVisibles = () => sortedSections.flatMap((section) => (
    (classesBySection[section.id] || []).filter((c) => (
      c.nom.toLowerCase().includes(rechercheNormalisee)
    ))
  ));

  const toggleAllClasses = () => {
    const visibles = classesVisibles().map((c) => c.id);
    const toutesCochees = visibles.length > 0 && visibles.every((id) => selectedClasses.includes(id));

    if (toutesCochees) {
      setSelectedClasses((prev) => prev.filter((id) => !visibles.includes(id)));
    } else {
      setSelectedClasses((prev) => [...new Set([...prev, ...visibles])]);
    }
  };

  const rechercheNormalisee = searchQuery.trim().toLowerCase();

  const classesBySection = classes.reduce((acc, classe) => {
    const sectionId = classe.section_id;
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(classe);
    return acc;
  }, {});

  const sortedSections = sections.sort((a, b) => {
    if (a.ordre && b.ordre) return a.ordre - b.ordre;
    return a.nom.localeCompare(b.nom);
  });

  const moisParTrimestre = moisScolaires.reduce((acc, mois) => {
    if (!acc[mois.trimestre]) {
      acc[mois.trimestre] = [];
    }
    acc[mois.trimestre].push(mois);
    return acc;
  }, {});

  // Ce qui manque encore pour que la portee veuille dire quelque chose.
  // Tant qu'il manque quelque chose, ON NE DEMANDE RIEN au serveur : il
  // refusait, et l'ecran affichait « Le nombre de creneaux n'a pas pu etre
  // calcule » AVANT que l'utilisateur ait rien saisi. Une erreur rouge sur
  // un ecran ou l'on vient d'arriver fait croire que quelque chose est casse.
  const porteeIncomplete = (() => {
    if (porteeType === 'mois' && !moisId) return 'Choisissez le mois concerné.';
    if (porteeType === 'trimestre' && !trimestre) return 'Choisissez le trimestre concerné.';
    if (porteeType === 'periode') {
      if (!dateDebut || !dateFin) return 'Choisissez une date de début et une date de fin.';
      // Comparaison de chaines « AAAA-MM-JJ » : juste, et sans objet Date.
      if (dateDebut > dateFin) return 'La date de début doit précéder la date de fin.';
    }
    return null;
  })();

  // Preview
  useEffect(() => {
    // ⚠️ Le compte d'AVANT ne vaut plus rien des que la selection change :
    // on l'efface tout de suite. Sinon il restait affiche pendant les 300 ms
    // d'attente - on cochait vingt classes de plus, le bouton disait encore
    // « Supprimer (36) », et il en partait des centaines.
    //
    // La confirmation retombe avec lui : un bouton arme sur un chiffre
    // perime est precisement ce qu'on ne veut pas.
    setPreviewCount(null);
    setConfirmation(false);

    const fetchPreview = async () => {
      if (selectedClasses.length === 0 || porteeIncomplete) {
        setPreviewCount(null);
        setPreviewMutualises(0);
        setGroupesMutualises([]);
        return;
      }

      try {
        const params = {
          classe_ids: selectedClasses.join(','),
          portee_type: porteeType,
          annee_scolaire_id: activeYear?.id
        };

        if (porteeType === 'trimestre') params.trimestre = trimestre;
        if (porteeType === 'mois') params.mois_id = moisId;
        if (porteeType === 'periode') {
          params.date_debut = dateDebut;
          params.date_fin = dateFin;
        }

        if (enseignantId) params.enseignant_id = enseignantId;

        const result = await api.timetable.previewBulkDelete(params);
        if (result.success) {
          setPreviewCount(result.data.count);
          setPreviewMutualises(result.data.count_mutualises || 0);
          const groupes = result.data.groupes_mutualises || [];
          setGroupesMutualises(groupes);
          // Une case cochee sur un cours qui n'est plus concerne par la
          // nouvelle selection n'aurait plus de sens : on l'oublie.
          setGroupesARetirer((avant) => avant.filter((id) => groupes.some((g) => g.id === id)));
        } else {
          // Sans ce `else`, le chiffre PRECEDENT restait affiche, accole au
          // libelle de la NOUVELLE portee : un compte faux, et credible.
          setPreviewCount(null);
          setPreviewMutualises(0);
          setGroupesMutualises([]);
          showToast(result?.message || "Le nombre de créneaux n'a pas pu être calculé.", 'error');
        }
      } catch (error) {
        // Un zero est un COMPTE, et il est credible : le bouton restait
        // actif sur un chiffre que personne n'avait calcule. Meme geste que
        // le `else` ci-dessus.
        console.error('Erreur preview:', error);
        setPreviewCount(null);
        setPreviewMutualises(0);
        setGroupesMutualises([]);
        showToast("Le nombre de créneaux n'a pas pu être calculé.", 'error');
      }
    };

    const timeoutId = setTimeout(fetchPreview, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedClasses, porteeType, trimestre, moisId, dateDebut, dateFin, activeYear, enseignantId, porteeIncomplete]);

  // Les enseignants ayant des creneaux dans les classes cochees. Rechargee a
  // chaque changement de selection : un enseignant retenu qui n'a plus de
  // creneau dans la nouvelle selection est oublie, sinon l'apercu afficherait
  // zero sans dire pourquoi.
  useEffect(() => {
    if (!isOpen || selectedClasses.length === 0 || !activeYear?.id) {
      setEnseignants([]);
      setEnseignantId('');
      return;
    }

    let abandonne = false;
    (async () => {
      const result = await api.timetable.getEnseignantsPourSuppression({
        classe_ids: selectedClasses.join(','),
        annee_scolaire_id: activeYear.id
      });
      if (abandonne) return;
      if (!result?.success) {
        showToast(result?.message || "La liste des enseignants n'a pas pu être lue.", 'error');
      }
      const liste = result?.success ? (result.data || []) : [];
      setEnseignants(liste);
      setEnseignantId((actuel) => (
        liste.some((e) => String(e.enseignant_id) === String(actuel)) ? actuel : ''
      ));
    })();
    return () => { abandonne = true; };
  }, [isOpen, selectedClasses, activeYear]);

  // Ce qui sera reellement efface : l'apercu, plus les creneaux des cours
  // partages dont on retire la classe.
  const totalASupprimer = (previewCount || 0) + groupesMutualises
    .filter((g) => groupesARetirer.includes(g.id))
    .reduce((somme, g) => somme + (g.nb_creneaux_vises || 0), 0);

  const handleSubmit = async () => {
    // Le verrou avant tout le reste : un double clic lancait DEUX
    // suppressions, et la seconde concluait « 0 creneau supprime avec
    // succes » sur une grille deja videe.
    if (!isValid || enSuppression) return;
    setEnSuppression(true);
    setConfirmation(false);

    // ✅ Toast de chargement
    const loadingToastId = Date.now();
    setToasts(prev => [...prev, { 
      id: loadingToastId, 
      message: 'Suppression en cours...', 
      type: 'loading' 
    }]);

    try {
      const params = {
        classe_ids: selectedClasses,
        portee_type: porteeType,
        annee_scolaire_id: activeYear?.id
      };

      if (porteeType === 'trimestre') params.trimestre = parseInt(trimestre);
      if (porteeType === 'mois') params.mois_id = parseInt(moisId);
      if (porteeType === 'periode') {
        params.date_debut = dateDebut;
        params.date_fin = dateFin;
      }

      if (enseignantId) params.enseignant_id = Number(enseignantId);
      if (groupesARetirer.length > 0) params.groupes_a_retirer = groupesARetirer;

      const result = await api.timetable.bulkDelete(params);
      
      if (result.success) {
        // ✅ Supprimer le toast de chargement
        removeToast(loadingToastId);
        
        // Le succes est annonce par le PARENT, qui recharge la grille dans
        // le meme mouvement. Les deux messages se superposaient au pixel :
        // Toast se portalise lui-meme en haut a droite, donc le conteneur
        // local n'y changeait rien.

        // ✅ Réinitialiser les sélections
        setSelectedClasses([]);
        setPreviewCount(null);
        
        // ✅ Notifier le parent SANS fermer le modal
        onDeleteSuccess(result.data);
      } else {
        // Sans ce `else`, un refus ne disait RIEN - et le toast
        // « Suppression en cours... » est de type `loading`, donc sans
        // minuterie : il restait a l'ecran indefiniment, un chargement
        // perpetuel sur une action qui avait deja echoue.
        //
        // handleResponse RETOURNE les erreurs metier au lieu de les lever
        // (services/api.js) : le `catch` ci-dessous ne les voit jamais.
        removeToast(loadingToastId);
        showToast(result?.message || "La suppression a été refusée par le serveur.", 'error');
      }
    } catch (error) {
      // ✅ Supprimer le toast de chargement
      removeToast(loadingToastId);
      
      // ✅ Afficher toast d'erreur
      console.error('Erreur suppression:', error);
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setEnSuppression(false);
    }
  };

  if (!isOpen) return null;

  // ⚠️ `previewCount !== null` fait partie de la validite, et ce n'est pas
  // une precaution de confort : sans lui, une classe cochee et la portee par
  // defaut - « Toute l'annee » - suffisaient a armer le bouton. On pouvait
  // effacer l'annee entiere d'une classe avant meme que le nombre de
  // creneaux concernes soit revenu du serveur.
  const isValid = selectedClasses.length > 0 && previewCount !== null && 
    (porteeType === 'annee' || 
     (porteeType === 'trimestre' && trimestre) ||
     (porteeType === 'mois' && moisId) ||
     (porteeType === 'periode' && dateDebut && dateFin));

  const getPorteeDescription = () => {
    if (porteeType === 'annee') return 'Toute l\'année scolaire';
    if (porteeType === 'trimestre') return `Trimestre ${trimestre}`;
    if (porteeType === 'mois') {
      const mois = moisScolaires.find(m => m.id === parseInt(moisId));
      return mois ? mois.nom : 'Mois sélectionné';
    }
    if (porteeType === 'periode' && dateDebut && dateFin) {
      return `Du ${dateDebut} au ${dateFin}`;
    }
    return '';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#c66d59] to-[#ae7467] text-white p-5 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <Trash2 size={24} />
              <h2 className="text-xl font-bold">Supprimer des emplois du temps</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={22} />
            </button>
          </div>

          {/* Bande d'information */}
          <div className="bg-red-50 border-b-2 border-red-200 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-gray-700 text-sm leading-relaxed">
                  <strong>Attention :</strong> Cette action est <strong>irréversible</strong>. 
                  Les créneaux supprimés ne pourront pas être récupérés. <strong>Tous les créneaux</strong> 
                  seront supprimés (actifs et annulés).
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* ÉTAPE 1 : Sélection des classes */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="bg-[#c66d59] text-white w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
                Sélectionner les classes ({selectedClasses.length} sélectionnée{selectedClasses.length > 1 ? 's' : ''})
              </h3>

              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={toggleAllClasses}
                  className="text-sm text-[#c66d59] hover:underline font-medium"
                >
                  {classesVisibles().length > 0
                  && classesVisibles().every((c) => selectedClasses.includes(c.id))
                  ? 'Tout désélectionner'
                  : 'Tout sélectionner'}
                </button>
              </div>

              {/* Barre de recherche */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une classe..."
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:border-[#c66d59] focus:outline-none"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Liste des classes */}
              <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                {sortedSections.map(section => {
                  const sectionClasses = (classesBySection[section.id] || []).filter(c =>
                    c.nom.toLowerCase().includes(rechercheNormalisee)
                  );

                  if (sectionClasses.length === 0) return null;

                  // Une recherche en cours OUVRE les sections qui
                  // correspondent : elles restaient repliees, et on croyait la
                  // recherche cassee.
                  const isExpanded = searchQuery.trim() !== '' || expandedSections.has(section.id);
                  const sectionSelectedCount = sectionClasses.filter(c => selectedClasses.includes(c.id)).length;

                  return (
                    <div key={section.id} className="border-b last:border-b-0">
                      <div
                        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleSection(section.id)}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                          <div className="text-[#c66d59]">
                            {getSectionIcon(section.nom)}
                          </div>
                          <span className="font-medium text-sm">{section.nom}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {sectionSelectedCount > 0 && (
                            <span className="text-xs bg-[#c66d59] text-white px-2 py-0.5 rounded-lg">
                              {sectionSelectedCount}/{sectionClasses.length}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {sectionClasses.length} classe{sectionClasses.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="bg-gray-50 p-3 space-y-2">
                          {sectionClasses.map(classe => {
                            const isSelected = selectedClasses.includes(classe.id);
                            // ⚠️ Le geste passe par l'input de la case, et PAS par un
                            // `onClick` sur le label : avec les deux, un clic
                            // basculerait la classe deux fois.
                            return (
                              <label
                                key={classe.id}
                                className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                              >
                                <CaseACocher
                                  cochee={isSelected}
                                  onChange={() => toggleClassSelection(classe.id)}
                                  label={classe.nom}
                                />
                                <span className="text-sm font-medium text-gray-900">{classe.nom}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ÉTAPE 2 : Portée */}
            {selectedClasses.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-[#c66d59] text-white w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
                  Choisir la portée de suppression
                </h3>

                <div className="space-y-3">
                  {/* Option: Année */}
                  <label 
                    className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: porteeType === 'annee' ? '#c66d59' : '#e5e7eb' }}
                    onClick={() => setPorteeType('annee')}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      porteeType === 'annee'
                        ? 'border-[#c66d59] bg-[#c66d59]'
                        : 'border-gray-400'
                    }`}>
                      {porteeType === 'annee' && (
                        <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Toute l'année scolaire</div>
                      <div className="text-sm text-gray-500 mt-1">
                        Supprimer tous les créneaux de l'année {activeYear?.nom}
                      </div>
                    </div>
                  </label>

                  {/* Option: Trimestre */}
                  <div 
                    className="border-2 rounded-lg overflow-hidden cursor-pointer transition-colors"
                    style={{ borderColor: porteeType === 'trimestre' ? '#c66d59' : '#e5e7eb' }}
                  >
                    <label 
                      className="flex items-start gap-3 p-4 hover:bg-gray-50"
                      onClick={() => setPorteeType('trimestre')}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                        porteeType === 'trimestre'
                          ? 'border-[#c66d59] bg-[#c66d59]'
                          : 'border-gray-400'
                      }`}>
                        {porteeType === 'trimestre' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-2">Un trimestre</div>
                      </div>
                    </label>
                    
                    {porteeType === 'trimestre' && (
                      <div className="px-4 pb-4">
                        <ChampSelect
                          valeur={trimestre}
                          onChange={(valeur) => setTrimestre(valeur)}
                          options={[
                            { valeur: '1', libelle: 'Trimestre 1' },
                            { valeur: '2', libelle: 'Trimestre 2' },
                            { valeur: '3', libelle: 'Trimestre 3' }
                          ]}
                        />
                      </div>
                    )}
                  </div>

                  {/* Option: Mois */}
                  <div 
                    className="border-2 rounded-lg overflow-hidden cursor-pointer transition-colors"
                    style={{ borderColor: porteeType === 'mois' ? '#c66d59' : '#e5e7eb' }}
                  >
                    <label 
                      className="flex items-start gap-3 p-4 hover:bg-gray-50"
                      onClick={() => setPorteeType('mois')}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                        porteeType === 'mois'
                          ? 'border-[#c66d59] bg-[#c66d59]'
                          : 'border-gray-400'
                      }`}>
                        {porteeType === 'mois' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-2">Un mois spécifique</div>
                      </div>
                    </label>
                    
                    {porteeType === 'mois' && (
                      <div className="px-4 pb-4">
                        {loadingMois ? (
                          <div className="text-sm text-gray-500">Chargement des mois...</div>
                        ) : (
                          <ChampSelect
                            valeur={moisId}
                            onChange={(valeur) => setMoisId(valeur)}
                            placeholder="Sélectionner un mois"
                            groupes={Object.entries(moisParTrimestre).map(([trim, moisList]) => ({
                              libelle: `Trimestre ${trim}`,
                              options: moisList.map((mois) => ({ valeur: mois.id, libelle: mois.nom }))
                            }))}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Option: Période */}
                  <div 
                    className="border-2 rounded-lg overflow-hidden cursor-pointer transition-colors"
                    style={{ borderColor: porteeType === 'periode' ? '#c66d59' : '#e5e7eb' }}
                  >
                    <label 
                      className="flex items-start gap-3 p-4 hover:bg-gray-50"
                      onClick={() => setPorteeType('periode')}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                        porteeType === 'periode'
                          ? 'border-[#c66d59] bg-[#c66d59]'
                          : 'border-gray-400'
                      }`}>
                        {porteeType === 'periode' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-2">Période personnalisée</div>
                      </div>
                    </label>
                    
                    {porteeType === 'periode' && (
                      <div className="px-4 pb-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Du</label>
                            <input
                              type="date"
                              value={dateDebut}
                              onChange={(e) => setDateDebut(e.target.value)}
                              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-[#c66d59] focus:outline-none"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Au</label>
                            <input
                              type="date"
                              value={dateFin}
                              onChange={(e) => setDateFin(e.target.value)}
                              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-[#c66d59] focus:outline-none"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Suppression ciblee : facultative, posee apres les classes et la
                portee. Sans choix, le comportement est celui d'avant. */}
            {selectedClasses.length > 0 && enseignants.length > 0 && (
              <div className="rounded-lg border-2 border-gray-200 p-4">
                <label htmlFor="enseignant-cible" className="block text-sm font-semibold text-gray-900">
                  Limiter à un enseignant
                </label>
                <p className="mt-0.5 text-xs text-gray-500">
                  Par défaut, les créneaux de tous les enseignants des classes cochées sont supprimés.
                </p>
                <div className="mt-2">
                  <ChampSelect
                    id="enseignant-cible"
                    valeur={enseignantId || TOUS_LES_ENSEIGNANTS}
                    onChange={(valeur) => setEnseignantId(valeur === TOUS_LES_ENSEIGNANTS ? '' : valeur)}
                    options={[
                      { valeur: TOUS_LES_ENSEIGNANTS, libelle: 'Tous les enseignants' },
                      ...enseignants.map((e) => ({
                        valeur: e.enseignant_id,
                        // Deux parts : le nom a gauche, le compte a droite. Colle
                        // au nom, il se lisait comme la suite de la phrase.
                        libelle: (
                          <span className="flex w-full items-center justify-between gap-6">
                            <span className="truncate">{e.nom_complet}</span>
                            <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-gray-500">
                              {e.nb_creneaux} créneau{e.nb_creneaux > 1 ? 'x' : ''}
                              {e.nb_mutualises > 0
                                ? ` (dont ${e.nb_mutualises} mutualisé${e.nb_mutualises > 1 ? 's' : ''})`
                                : ''}
                            </span>
                          </span>
                        )
                      }))
                    ]}
                  />
                </div>
              </div>
            )}

            {/* Ce qui manque encore. Un ton neutre, et pas une erreur rouge :
                l'utilisateur n'a rien fait de mal, il n'a pas encore fini. */}
            {porteeIncomplete && selectedClasses.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                {porteeIncomplete}
              </div>
            )}

            {/* Aperçu */}
            {previewCount !== null && selectedClasses.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-900 mb-1">Attention : Action irréversible</h4>
                    <p className="text-sm text-red-700">
                      {/* Les cases cochees ajoutent leurs creneaux au total : sans
                          cela l'apercu annoncerait moins que ce qu'il efface. */}
                      Cette action supprimera <strong>{totalASupprimer} créneau{totalASupprimer > 1 ? 'x' : ''}</strong> pour{' '}
                      <strong>{selectedClasses.length} classe{selectedClasses.length > 1 ? 's' : ''}</strong>
                      {' '}({getPorteeDescription()}).
                    </p>
                    <p className="text-xs text-red-600 mt-2">
                      Tous les créneaux seront supprimés, y compris les créneaux annulés.
                    </p>
                    {groupesMutualises.length > 0 && (
                      <div className="mt-2 rounded-lg bg-white/70 px-3 py-2.5 text-xs text-gray-700">
                        <p className="font-semibold text-gray-900">
                          {previewMutualises} créneau{previewMutualises > 1 ? 'x' : ''} de cet enseignant
                          {' '}{previewMutualises > 1 ? 'appartiennent' : 'appartient'} à un cours partagé
                          {' '}avec d'autres classes.
                        </p>
                        <p className="mt-0.5">
                          Ils ne seront pas supprimés, sauf à retirer cette classe du cours partagé.
                          Les autres classes gardent le leur.
                        </p>

                        <ul className="mt-2 space-y-1.5">
                          {groupesMutualises.map((g) => (
                            <li key={g.id} className="rounded-lg bg-white px-2.5 py-2">
                              <label className="flex items-start gap-2 cursor-pointer">
                                <CaseACocher
                                  cochee={groupesARetirer.includes(g.id)}
                                  onChange={() => setGroupesARetirer((avant) => (
                                    avant.includes(g.id) ? avant.filter((id) => id !== g.id) : [...avant, g.id]
                                  ))}
                                  label={`Retirer ${g.classes_visees || 'cette classe'} du cours partagé`}
                                  className="mt-0.5"
                                />
                                <span className="min-w-0">
                                  <span className="block font-semibold text-gray-900">{g.libelle}</span>
                                  <span className="block text-gray-600">{g.quand}</span>
                                  <span className="block text-gray-600">
                                    Retirer <strong>{g.classes_visees || 'cette classe'}</strong> du cours partagé
                                    {' '}et supprimer ses {g.nb_creneaux_vises} créneau{g.nb_creneaux_vises > 1 ? 'x' : ''}.
                                  </span>
                                  <span className="block text-emerald-700">
                                    {!g.classes_conservees
                                      ? 'Toutes les classes de ce cours partagé sont visées : le partage disparaît.'
                                      : g.restera_partage
                                        ? `${g.classes_conservees} gardent leurs créneaux et le cours partagé.`
                                        : `${g.classes_conservees} garde ses créneaux ; restée seule, elle n'a plus de cours partagé.`}
                                  </span>
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pied. La confirmation se fait ICI, en deux temps, et pas dans
              une fenetre : la coquille de cet ecran est encore un calque fait
              main, et un dialogue des primitives pose dedans rendrait tout le
              calque inerte derriere lui. Un second clic delibere, qui redit le
              nombre et la portee, protege aussi bien. */}
          <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex items-center justify-between gap-4">
            <button
              onClick={() => (confirmation ? setConfirmation(false) : onClose())}
              disabled={enSuppression}
              className="px-6 py-2.5 rounded-lg border-2 border-gray-300 hover:bg-gray-100 font-medium transition-colors disabled:opacity-50"
            >
              {confirmation ? 'Annuler' : 'Fermer'}
            </button>

            {confirmation ? (
              <div className="flex min-w-0 items-center gap-3">
                <span className="truncate text-sm text-gray-700">
                  {totalASupprimer} créneau{totalASupprimer > 1 ? 'x' : ''} - {getPorteeDescription()}
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={enSuppression}
                  className="flex shrink-0 items-center gap-2 rounded-lg bg-[#c66d59] px-6 py-2.5 font-medium text-white transition-colors hover:bg-[#ae7467] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Trash2 className="w-4 h-4" />
                  {enSuppression ? 'Suppression...' : 'Confirmer la suppression'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmation(true)}
                disabled={!isValid}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-white font-medium transition-all ${
                  !isValid
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'hover:opacity-90'
                }`}
                style={isValid ? {
                  background: 'linear-gradient(to right, #c66d59, #ae7467)'
                } : undefined}
              >
                <Trash2 className="w-4 h-4" />
                Supprimer {totalASupprimer > 0 ? `(${totalASupprimer})` : ''}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ✅ Affichage des Toasts */}
      <div className="fixed top-4 right-4 z-[10000] space-y-2">
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            message={toast.message} 
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </>
  );
};

export default DeleteBulkTimetableModal;